import React, { useEffect, useMemo, forwardRef, useCallback } from 'react';
import { Alert, Button, Form } from 'antd'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes, generateFormFields, parseSelectOptions } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';
import _ from 'lodash';
import { useRef } from 'react';
import { useState } from 'react';

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  projectList: [{ project: undefined }]
};

export const DELIMITER = "||";
export const NODE_DELIMITER = ";"

const TaniumMappingForm = forwardRef(({disableProjectFields = false, ...props}, ref) => {
  const projectsRef = useRef({});
  const nodesRef = useRef({});
  const nodesApiRef = useRef([]);
  const [form] = Form.useForm();
  const cancelRef = useRef([]);
  const toolConfigId = props.record ? props.record.id : undefined;
  const isTaniumConnect = props.loginConfig?.bucketName;
  const isApiCredentials = props.loginConfig?.apiEndpoint;
  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [{ ...initialFormValues }];
  });
  const [nodeChanged, setNodeChanged] = useState(false);

  const [mappedProjectNodeList] = useState(() => {
    // this state contains the already mapped projects/nodes
    // This is stored because the project and node dropdowns are paginated
    // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
    if(props.record){
      const { nodeName, nodeId } = props.record;
      if(_.isEmpty(nodeName) && _.isEmpty(nodeId)){
        return { projects: [], nodes: {} };
      }
      const projectNodeNameArray = nodeName.split(",");
      const projectNodeIdArray = nodeId.split(",");
      const projects = [], nodes = {}
      _.forEach(projectNodeIdArray, (projectNodeId, i) => {
        const [projectId, nodeId] = projectNodeId.split(DELIMITER);
        const index = projectNodeNameArray[i].lastIndexOf(DELIMITER)
  
        const projectName = projectNodeNameArray[i].substring(0, index === -1 ? undefined : index);
        const nodeName = index === -1 ? undefined : projectNodeNameArray[i].substring(index + 1)
        projects.push({ id: projectId, name: projectName });
        projectsRef.current = {...projectsRef.current, [projectId]: projectName }
        if(nodeId){
          const nodeNameArray = nodeName.split(NODE_DELIMITER)
          _.forEach(nodeId.split(NODE_DELIMITER), (noId, index) => {
            nodes[nodeId] = { id: noId, name: nodeNameArray[index] }
            nodesRef.current = {...nodesRef.current, [noId]: nodeNameArray[index] }
          })
        }
      });
      return {
        projects: _.uniqBy(projects, "id"),
        nodes
      }
    }
    return { projects: [], nodes: {} }
  })

  const [groupOptions, setGroupOptions] = useState(mappedProjectNodeList.projects)
  useEffect(() => {
    // cancelling all node APIs on component unmount
    const cancels = cancelRef.current;
    return () => _.forEach(cancels, cancel => cancel());
  }, []);

  const fetchNode = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, toolConfigId,  ...params });
    return {
      promise: promise.then(response => {
        // merging the already mapped nodes to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if(mappedProjectNodeList.nodes[params.project] && !_.isEmpty(response.projects)){
          response.projects = [...response.projects, mappedProjectNodeList.nodes[params.project]];
        }
        _.forEach(response.projects, p => {
          nodesRef.current[p.id] = p.name;
        });
        // merging the already mapped nodes to the fetched list so that if the selected value is not in the option list
        // then id should not be displayed
        if(mappedProjectNodeList.nodes[params.project] && !_.isEmpty(response.projects)){
          response.projects = [...response.projects, mappedProjectNodeList.nodes[params.project]];
        }
        return response;
      }),
      cancel
    }
  }, [props.loginConfigId, props.toolName, mappedProjectNodeList, toolConfigId]);

  const fetchProjects = useCallback((params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, toolConfigId, ...params });

    promise.then(response => {
      // merging the already mapped nodes to the fetched list so that if the selected value is not in the option list
      // then id should not be displayed
      const safeProjects = Array.isArray(response.projects) ? response.projects : [];

      if (mappedProjectNodeList.projects.length > 0 && !_.isEmpty(response.projects)) {
        response.projects = [...safeProjects, ...mappedProjectNodeList.projects];
      } else {
        response.projects = safeProjects;
      }
      _.forEach(response.projects, p => {
        projectsRef.current[p.id] = p.name;
      });
      setGroupOptions(proj => (_.uniqBy([...proj, ...response.projects], 'id')))
      return response;
    })
    return cancel

  }, [props.loginConfigId, props.toolName, mappedProjectNodeList, toolConfigId]);

  useEffect(fetchProjects, [fetchProjects]);

  const getExtraFieldsData = useCallback((config) => {
    const { projectList } = config;

    const projectNodeNameList = [];
    const nodeId = _.map(_.filter(projectList, ({ project }) => project !== undefined), ({ project, node }) => {
      const projectName = projectsRef.current[project];
      const nodeName = _.isArray(node)? _.map(node, v => nodesRef.current[v]).join(NODE_DELIMITER) : nodesRef.current[node];
      if(!_.isEmpty(node)){
        projectNodeNameList.push(projectName + DELIMITER + nodeName);
        return `${project}${DELIMITER}${_.isArray(node) ? node.join(NODE_DELIMITER): node}`;
      }
      projectNodeNameList.push(projectName);
      return project;
    });

    
    return ({
      nodeId: nodeId.join(),
      nodeName: projectNodeNameList.join(),
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  }, []);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { nodeId } = config;
    const projectWithNodes = nodeId?.split(',');
    const projects = _.map(projectWithNodes, projectWithNode => {
      if(_.isEmpty(projectWithNode)){
        return {project: undefined};
      }
      const index = projectWithNode.lastIndexOf(DELIMITER);
      if(index !== -1){
        const project = projectWithNode.substring(0, index);
        const node = projectWithNode.substring(index + 2, projectWithNode.length); // +2 beacuse each index for ||
        return { project, node: node.split(NODE_DELIMITER) };
      }
      return { project: projectWithNode }
    });
    return ({
      projectList: projects,
    });
  }, []);
 
  const onValuesChange = useCallback((values, ...args) => {
    if(values.configurations){
      let isProjectChanged = false;
        const formValues = form.getFieldsValue();
        _.forEach(values.configurations, (configs, index) => {

          // If project list is greater than 1, means that a new blank row is added
          // no need to clear the node fields
          if(!configs || !configs.projectList || configs.projectList.length > 1){
            return;
          }

          // since this is a list field, onchange will trigger only for the changed row.
          // if change is done at 2nd row, data received in this callback will only contain 2nd row data
          // configs.projectList is given as string when we search in the field
          if(!configs || _.isString(configs.projectList)){
            return;
          }
          _.forEach(configs.projectList, (object, i) => {

            // since this is a list field, onchange will trigger only for the changed row.
            // if change is done at 2nd row, data received in this callback will only contain 2nd row data
            // Also checking if the object contains `project` and nothing else 
            // because we need to reset the node field only if the project is changed
            // and this change is also triggered when rows are added or deleted, so we need to ignore it
            if(object && object.project && Object.keys(object).length === 1){
              isProjectChanged = true;
              formValues.configurations[index].projectList[i].node = undefined;
            }
          })
        });
        if(isProjectChanged){
          form.setFieldsValue(formValues);
        }
    }
  }, [form]);

  const projectListRender = useCallback((listField) => {

    // This is a hack, we are storing the api method for node in a ref
    // as it is dependant on `listField` param of this method
    // that is why we cannot move it to `useCallback`
    // For a `listField.name` we are defining the method only once and using the method if it alreadr exists in the ref
    // Reason for doing this is that the method reference will always change and will trigger API on every render
    // It also causes a visible bug on UI as when the API triggers, the selected value disappears as we don't show value when data is loading
    let nodeAPI = nodesApiRef.current[listField.name];
    if(!nodeAPI){
      nodeAPI = nodesApiRef.current[listField.name] = ({ listFieldName: nestedListFieldName, ...params }) => {
        const allConfigs = form.getFieldsValue().configurations;
        const project = allConfigs[listField.name]?.projectList[nestedListFieldName]?.project;
        if (!project) {
          return { promise: Promise.resolve([]), cancel: _.noop };
        }
        return fetchNode({ ...params, project: projectsRef.current[project] });
      }
    }
    // 

    const __fields = [{
      name: "project",
      placeholder: (isTaniumConnect && !isApiCredentials) ? 'Leave blank to select all' : 'project',
      type: FieldTypes.SELECT,
      disabled: disableProjectFields,
      colSpan: 9,
      loading: _.isUndefined(groupOptions),
      options: parseSelectOptions(groupOptions),
      props: {
        autoFocus: !disableProjectFields,
        optionsMutator: (projects) => {
          const selected = _.compact(_.map(form.getFieldsValue().configurations?.[listField.name]?.projectList, p => p?.project));
          return _.map(projects, p => ({ ...p, disabled: _.includes(selected, p.id) }));
        },
      },
      rules: [{ required: !isTaniumConnect, message: 'Tanium Project is required' }]
    }, ...(isTaniumConnect ? [] : [{
      name: 'node',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      disabled: disableProjectFields,
      colSpan: 9,
      props: {
        api: nodeAPI,
        mode: "multiple",
        setExtraProps: (field, nestedListField) => {
          const allConfigs = form.getFieldsValue().configurations;
          const project = allConfigs[listField.name]?.projectList[nestedListField.name]?.project;
          return {
            showValueOnload: false,
            resetOptionsProp: project
          }
        },
        onChange: () => {
          setNodeChanged(!_.isEmpty(props.record))
        }
      },
    }]), {
      name: "add",
      colSpan: 2,
      render: ({ remove, name, fields, add }) => {
        if(isTaniumConnect){
          if (!isApiCredentials) {
            return ;
          }
          return <Button disabled={fields.length === 0 } type='dashed' onClick={() => { handleLastDelete(fields, remove, name, add) }}>-</Button>;
        }
        else{
          return <Button disabled={fields.length === 1} type='dashed' onClick={() => { remove(name) }}>-</Button>;
        }
      }
    }]

    const handleLastDelete = (fields, remove, name, add) => {
      remove(name);
      if(fields.length === 1){
        add({ project: undefined });
      }
    }
    
    return (
      <div className='m-b-sm'>
        <Form.List name={[listField.name, "projectList"]}>
          {(fields, { add, remove }) => {
            return (
              <div>
                {_.map(fields, field => {
                  return generateFormFields(__fields, 9, { ...field/* , name: [listField.name, field.name] */, add, remove, fields }, field.name);
                })}
                { isApiCredentials &&
                  <Button size='small' type='dashed' onClick={() => add({ project: undefined })} disabled={disableProjectFields}>+ Add Project</Button>
                }
                {nodeChanged && <Alert className="m-t-m" message="Adding/removing node from an existing mapping might resolve findings from the previous mapped node." type="warning" />}
              </div>
            )
          }}
        </Form.List>
      </div>
    )
  }, [disableProjectFields, groupOptions, isTaniumConnect, form, fetchNode, props.record, isApiCredentials, nodeChanged]);


  const _fields = useMemo(() => [{
    name: "label",
    type: FieldTypes.LABEL,
    value: (isTaniumConnect) ? <b>Computer Groups</b> : <b className='required-mark'>Computer Groups</b>,
    style: {marginBottom: -24},
    colSpan: 9
  }, ...(isTaniumConnect ? [] : [{
    name: "label",
    type: FieldTypes.LABEL,
    value: <b>Node Name</b>,
    style: {marginBottom: -24},
    colSpan: 9
  }]), {
    name: (listField) => [listField.name, "projectList"],
    render: projectListRender
  },
   {
    type: FieldTypes.DIVIDER
  }], [projectListRender, isTaniumConnect]);

  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={_fields}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
      {...props}
      onValuesChange={onValuesChange}
    />
  )
})

export default TaniumMappingForm;